import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Select,
  Input,
  Button,
  Spin,
  Empty,
  notification,
  Segmented,
  Tooltip,
  Tabs,
  Dropdown,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import { numberWithCommas } from "util/common";
import {
  ENGAGEMENT_CATEGORY_LABEL_KEYS,
  ENGAGEMENT_EVENT_LABEL_KEYS,
  exportEngagementCsv,
  exportEngagementSessionsCsv,
  exportEngagementJson,
  exportEngagementExcel,
  exportEngagementSessionsExcel,
  filterSessionTimelineEvents,
  filterEngagementTimelineEvents,
  formatVisitorFormSnapshotSummary,
  summarizeVisitorAuthActivity,
  formatEngagementDateTime,
  formatEngagementDuration,
  formatEngagementFlow,
  formatEngagementTime,
  formatSessionIdShort,
  formatEngagementEventDetail,
  formatEngagementPropertyValue,
  getEngagementLevelKey,
  getEventDwellDisplay,
  getEventPreviousPage,
  getPageViewDwellStats,
  maxDailyActive,
  normalizeEngagementPagePath,
  renderEngagementPropertyLabel,
} from "util/engagementDisplay";
import styles from "./style.module.scss";

const PERIOD_DAYS = {
  "7": 7,
  "30": 30,
  "90": 90,
};

const ENGAGEMENT_EXPORT_PAGE_SIZE = 100;

const SUMMARY_METRICS_KNOWN = [
  { valueKey: "dau", labelKey: "statDau", hintKey: "statDauHint" },
  { valueKey: "wau", labelKey: "statWau", hintKey: "statWauHint" },
  { valueKey: "mau", labelKey: "statMau", hintKey: "statMauHint" },
  { valueKey: "total_events", labelKey: "statEvents", hintKey: "statEventsHint" },
  { valueKey: "total_sessions", labelKey: "statSessions", hintKey: "statSessionsHint" },
  {
    valueKey: "active_users_in_period",
    labelKey: "statActiveUsers",
    hintKey: "statActiveUsersHint",
  },
];

const SUMMARY_METRICS_UNKNOWN = [
  { valueKey: "dau", labelKey: "statDailyVisitors", hintKey: "statDailyVisitorsHint" },
  { valueKey: "wau", labelKey: "statWeeklyVisitors", hintKey: "statWeeklyVisitorsHint" },
  { valueKey: "mau", labelKey: "statMonthlyVisitors", hintKey: "statMonthlyVisitorsHint" },
  { valueKey: "total_events", labelKey: "statEvents", hintKey: "statUnknownEventsHint" },
  { valueKey: "total_sessions", labelKey: "statSessions", hintKey: "statUnknownSessionsHint" },
  {
    valueKey: "active_users_in_period",
    labelKey: "statUniqueVisitors",
    hintKey: "statUniqueVisitorsHint",
  },
];

const renderUserCell = (record, t, stylesRef, isUnknownAudience = false) => {
  if (isUnknownAudience) {
    const label =
      record.visitor_label ||
      (record.visitor_key ? `Visitor ${String(record.visitor_key).slice(0, 8)}` : null);
    if (label) {
      return (
        <Tooltip title={record.visitor_key || label}>
          <span className={stylesRef.userName}>{label}</span>
        </Tooltip>
      );
    }
  }
  if (record.user_display || record.user_email) {
    return (
      <div className={stylesRef.userCell}>
        <span className={stylesRef.userName}>
          {record.user_display || record.user_email}
        </span>
        {record.user_email && record.user_display ? (
          <span className={stylesRef.userEmail}>{record.user_email}</span>
        ) : null}
      </div>
    );
  }
  if (record.user_id) {
    return <span className={stylesRef.userEmail}>{record.user_id}</span>;
  }
  return <span className={stylesRef.systemUser}>{t("engagement_page.systemUser")}</span>;
};

const BotSuspectBadge = ({ record, t }) => {
  const suspect = record?.bot_suspect || record?.properties?.bot_suspect;
  if (!suspect) return null;
  const score = record?.bot_score ?? record?.properties?.bot_score;
  const signals = record?.bot_signals || record?.properties?.bot_signals || [];
  return (
    <Tooltip
      title={t("engagement_page.botSuspectTooltip", {
        score: score ?? "—",
        signals: Array.isArray(signals) && signals.length ? signals.join(", ") : "—",
      })}
    >
      <span className={styles.botSuspectBadge}>{t("engagement_page.botSuspectBadge")}</span>
    </Tooltip>
  );
};

const EngageBadge = ({ actionCount, t }) => {
  const levelKey = getEngagementLevelKey(actionCount);
  const levelClass =
    actionCount >= 3
      ? styles.engageHigh
      : actionCount >= 1
        ? styles.engageMedium
        : styles.engageLow;
  return (
    <Tooltip
      title={
        actionCount > 0
          ? t("engagement_page.engageTooltipActions", { count: actionCount })
          : t("engagement_page.engageTooltipPassive")
      }
    >
      <span className={`${styles.engageBadge} ${levelClass}`}>{t(levelKey)}</span>
    </Tooltip>
  );
};

const SessionTimeline = ({ session, t, locale, renderEventLabel, renderCategoryLabel }) => (
  <ol className={styles.sessionTimeline}>
    {filterSessionTimelineEvents(session.events).map((ev) => {
      const previous = getEventPreviousPage(ev);
      const flow = formatEngagementFlow(ev);
      const dwell = getEventDwellDisplay(ev, session.events, t);
      return (
        <li key={ev.id || `${ev.created_at}-${ev.event_name}`} className={styles.sessionTimelineItem}>
          <span className={styles.sessionTimelineTime}>
            {formatEngagementTime(ev.created_at, locale)}
          </span>
          <div className={styles.sessionTimelineBody}>
            <div className={styles.sessionTimelineHead}>
              <span className={styles.categoryBadge}>{renderCategoryLabel(ev.event_category)}</span>
              <strong>{renderEventLabel(ev.event_name)}</strong>
              {ev.event_name === "page_view" && dwell.durationMs ? (
                <span
                  className={`${styles.dwellBadge} ${dwell.inferred ? styles.dwellInferred : ""}`}
                  title={
                    dwell.inferred
                      ? t("engagement_page.durationInferredHint")
                      : undefined
                  }
                >
                  {formatEngagementDuration(dwell.durationMs, t)}
                </span>
              ) : null}
              {ev.event_name === "page_view" ? (
                <EngageBadge actionCount={dwell.actionCount} t={t} />
              ) : null}
            </div>
            {formatEngagementEventDetail(ev, t) !== "—" ? (
              <div className={styles.sessionTimelineDetail}>
                {formatEngagementEventDetail(ev, t)}
              </div>
            ) : null}
            {flow !== "—" ? (
              <div className={styles.sessionTimelineFlow} title={ev.page_path || flow}>
                {previous && previous !== ev.page_path ? (
                  <>
                    <span className={styles.flowFrom}>{normalizeEngagementPagePath(previous)}</span>
                    <ArrowRightOutlined className={styles.flowArrow} />
                    <span className={styles.flowTo}>
                      {normalizeEngagementPagePath(ev.page_path || previous)}
                    </span>
                  </>
                ) : (
                  <span>{flow}</span>
                )}
              </div>
            ) : null}
          </div>
        </li>
      );
    })}
  </ol>
);

const Representation = ({ user, locale = "tr-TR" }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const [summary, setSummary] = useState(null);
  const [dailyActive, setDailyActive] = useState([]);
  const [topEvents, setTopEvents] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [items, setItems] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [topAuthEvents, setTopAuthEvents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0 });
  const [filterUsers, setFilterUsers] = useState([]);

  const [period, setPeriod] = useState("30");
  const [filterUserId, setFilterUserId] = useState(undefined);
  const [categoryFilter, setCategoryFilter] = useState(undefined);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("events");
  const [botTrafficFilter, setBotTrafficFilter] = useState("human");
  const [botSuspectSessionsInPeriod, setBotSuspectSessionsInPeriod] = useState(0);
  const [botTrafficExcluded, setBotTrafficExcluded] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [audienceTab, setAudienceTab] = useState("known");
  const [exporting, setExporting] = useState(false);
  const [securitySummary, setSecuritySummary] = useState(null);
  const [ipBans, setIpBans] = useState([]);
  const [authAttempts, setAuthAttempts] = useState([]);
  const [securityView, setSecurityView] = useState("bans");
  const [mongodbReady, setMongodbReady] = useState(true);

  const isBlockedView = audienceTab === "blocked";
  const isUnknownAudience = audienceTab === "unknown";
  const isKnownAudience = audienceTab === "known";
  const summaryMetrics = isUnknownAudience ? SUMMARY_METRICS_UNKNOWN : SUMMARY_METRICS_KNOWN;

  const buildAnalyticsPayload = useCallback(
    (page, pageSize) => {
      const payload = {
        page,
        page_size: pageSize,
        period_days: PERIOD_DAYS[period] || 30,
        view_mode: viewMode,
        exclude_bot_suspect: isKnownAudience ? true : botTrafficFilter === "human",
        bot_suspect_only: isUnknownAudience && botTrafficFilter === "bot",
        anonymous_only: isUnknownAudience,
      };
      if (filterUserId) payload.filter_user_id = filterUserId;
      if (categoryFilter) payload.event_category = categoryFilter;
      if (search.trim()) payload.search = search.trim();
      return payload;
    },
    [
      period,
      viewMode,
      botTrafficFilter,
      isUnknownAudience,
      isKnownAudience,
      filterUserId,
      categoryFilter,
      search,
    ],
  );

  const fetchAllForExport = useCallback(async () => {
    let page = 1;
    let totalPages = 1;
    const allItems = [];
    const allSessions = [];

    while (page <= totalPages) {
      const result = await apiRequest(
        "organization_engagement/analytics",
        "POST",
        buildAnalyticsPayload(page, ENGAGEMENT_EXPORT_PAGE_SIZE),
        true,
      );
      if (!result?.success) {
        throw new Error(result?.error || "export_failed");
      }
      if (viewMode === "sessions") {
        allSessions.push(...(result.sessions || []));
      } else if (viewMode === "visitors") {
        allSessions.push(...(result.visitors || []));
      } else {
        allItems.push(...(result.items || []));
      }
      const pag = result.pagination || {};
      const pageSize = pag.page_size || ENGAGEMENT_EXPORT_PAGE_SIZE;
      totalPages = Math.max(1, Math.ceil((pag.total || 0) / pageSize));
      page += 1;
    }

    return { items: allItems, sessions: allSessions };
  }, [buildAnalyticsPayload, viewMode]);

  const fetchSecurityBlocks = useCallback(
    async (page = 1) => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const result = await apiRequest(
          "organization_engagement/security/blocks",
          "POST",
          {
            page,
            page_size: 50,
            period_days: PERIOD_DAYS[period] || 30,
            view: securityView,
            search: search.trim() || undefined,
          },
          true,
        );
        if (result?.success) {
          setSecuritySummary(result.summary || null);
          setIpBans(result.bans || []);
          setAuthAttempts(result.attempts || []);
          setPagination(result.pagination || { page: 1, page_size: 50, total: 0 });
          setMongodbReady(result.mongodb_ready !== false);
          setForbidden(false);
        } else if (
          result?.error &&
          String(result.error).toUpperCase().includes("FORBIDDEN")
        ) {
          setForbidden(true);
        } else {
          notification.error({ message: t("engagement_page.loadError") });
        }
      } catch {
        notification.error({ message: t("engagement_page.loadError") });
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, period, securityView, search, t],
  );

  const fetchAnalytics = useCallback(
    async (page = 1) => {
      if (isBlockedView) {
        return fetchSecurityBlocks(page);
      }
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const payload = buildAnalyticsPayload(
          page,
          viewMode === "events" ? 50 : 20,
        );

        const result = await apiRequest(
          "organization_engagement/analytics",
          "POST",
          payload,
          true,
        );

        if (result?.success) {
          setSummary(result.summary || null);
          setDailyActive(result.daily_active || []);
          setTopEvents(result.top_events || []);
          setTopPages(result.top_pages || []);
          setItems(result.items || []);
          setSessions(result.sessions || []);
          setVisitors(result.visitors || []);
          setTopAuthEvents(result.top_auth_events || []);
          setPagination(result.pagination || { page: 1, page_size: 50, total: 0 });
          setFilterUsers(result.filter_users || []);
          setBotSuspectSessionsInPeriod(result.bot_suspect_sessions_in_period || 0);
          setBotTrafficExcluded(Boolean(result.bot_traffic_excluded));
          setFunnel(result.funnel || null);
          setForbidden(false);
        } else if (
          result?.error &&
          String(result.error).toUpperCase().includes("FORBIDDEN")
        ) {
          setForbidden(true);
        } else {
          notification.error({
            message: result?.error || t("engagement_page.loadError"),
          });
        }
      } catch {
        notification.error({ message: t("engagement_page.loadError") });
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, buildAnalyticsPayload, viewMode, t, isBlockedView, fetchSecurityBlocks],
  );

  useEffect(() => {
    if (audienceTab === "unknown") {
      setViewMode("visitors");
      setBotTrafficFilter("bot");
    } else if (audienceTab === "known") {
      setViewMode("events");
    } else if (audienceTab === "blocked") {
      setSecurityView("bans");
    }
  }, [audienceTab]);

  useEffect(() => {
    if (isUnknownAudience) {
      setFilterUserId(undefined);
    }
  }, [isUnknownAudience]);

  useEffect(() => {
    if (isBlockedView) {
      fetchSecurityBlocks(1);
      return;
    }
    fetchAnalytics(1);
  }, [fetchAnalytics, fetchSecurityBlocks, isBlockedView]);

  const dailyMax = useMemo(() => maxDailyActive(dailyActive), [dailyActive]);

  const displayItems = useMemo(
    () => items.filter((row) => row.event_name !== "page_dwell"),
    [items],
  );

  const sessionItemsBySessionId = useMemo(() => {
    const map = new Map();
    items.forEach((row) => {
      if (!row.session_id) return;
      const list = map.get(row.session_id) || [];
      list.push(row);
      map.set(row.session_id, list);
    });
    return map;
  }, [items]);

  const renderEventLabel = (name) =>
    t(ENGAGEMENT_EVENT_LABEL_KEYS[name] || name, name);

  const renderCategoryLabel = (cat) =>
    t(ENGAGEMENT_CATEGORY_LABEL_KEYS[cat] || cat, cat);

  const renderExpandedRow = (record) => {
    const props = record.properties || {};
    const entries = Object.entries(props);
    if (!entries.length) {
      return (
        <div className={styles.expandedPanel}>
          <p className={styles.expandedEmpty}>{t("engagement_page.detailEmpty")}</p>
        </div>
      );
    }
    return (
      <div className={styles.expandedPanel}>
        <div className={styles.detailGrid}>
          {entries.map(([key, val]) => (
            <React.Fragment key={key}>
              <div className={styles.detailLabel}>
                {renderEngagementPropertyLabel(key, t)}
              </div>
              <div className={styles.detailValue}>
                {formatEngagementPropertyValue(val, t)}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: t("engagement_page.colDate"),
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (val) => formatEngagementDateTime(val, locale),
    },
    {
      title: t("engagement_page.colUser"),
      key: "user",
      width: 180,
      render: (_, record) => renderUserCell(record, t, styles, isUnknownAudience),
    },
    {
      title: t("engagement_page.colEvent"),
      dataIndex: "event_name",
      key: "event_name",
      width: 170,
      render: (name, record) => (
        <span className={styles.eventCell}>
          {renderEventLabel(name)}
          <BotSuspectBadge record={record} t={t} />
        </span>
      ),
    },
    {
      title: t("engagement_page.colCategory"),
      dataIndex: "event_category",
      key: "event_category",
      width: 120,
      render: (cat) => (
        <span className={styles.categoryBadge}>{renderCategoryLabel(cat)}</span>
      ),
    },
    {
      title: t("engagement_page.colFlow"),
      key: "flow",
      width: 220,
      ellipsis: true,
      render: (_, record) => {
        const flow = formatEngagementFlow(record);
        if (flow === "—") return "—";
        const previous = getEventPreviousPage(record);
        if (previous && record.page_path && previous !== record.page_path) {
          return (
            <span className={styles.flowCell} title={record.page_path}>
              <span className={styles.flowFrom}>{normalizeEngagementPagePath(previous)}</span>
              <ArrowRightOutlined className={styles.flowArrow} />
              <span className={styles.flowTo}>{normalizeEngagementPagePath(record.page_path)}</span>
            </span>
          );
        }
        return <span className={styles.flowCell}>{flow}</span>;
      },
    },
    {
      title: t("engagement_page.colIp"),
      key: "client_ip",
      width: 130,
      render: (_, record) => {
        const ip = record.client_ip || record.properties?.client_ip;
        return ip ? (
          <Tooltip title={ip}>
            <span className={styles.ipCell}>{ip}</span>
          </Tooltip>
        ) : (
          "—"
        );
      },
    },
    {
      title: t("engagement_page.colDetails"),
      key: "details",
      width: 240,
      ellipsis: true,
      render: (_, record) => {
        const detail = formatEngagementEventDetail(record, t);
        return detail !== "—" ? (
          <span className={styles.eventDetailCell} title={detail}>
            {detail}
          </span>
        ) : (
          "—"
        );
      },
    },
    {
      title: t("engagement_page.colDuration"),
      key: "duration",
      width: 110,
      render: (_, record) => {
        const sessionEvents = sessionItemsBySessionId.get(record.session_id) || items;
        const dwell = getEventDwellDisplay(record, sessionEvents, t);
        if (!dwell.durationMs) return "—";
        return (
          <Tooltip
            title={dwell.inferred ? t("engagement_page.durationInferredHint") : undefined}
          >
            <span className={dwell.inferred ? styles.dwellInferred : styles.dwellBadge}>
              {formatEngagementDuration(dwell.durationMs, t)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t("engagement_page.colEngagement"),
      key: "engagement",
      width: 120,
      render: (_, record) => {
        if (record.event_name !== "page_view") return "—";
        const sessionEvents = sessionItemsBySessionId.get(record.session_id) || items;
        const dwell = getPageViewDwellStats(record, sessionEvents);
        return <EngageBadge actionCount={dwell.actionCount} t={t} />;
      },
    },
    {
      title: t("engagement_page.colPage"),
      dataIndex: "page_path",
      key: "page_path",
      ellipsis: true,
      render: (val) => val || "—",
    },
    {
      title: t("engagement_page.colSession"),
      dataIndex: "session_id",
      key: "session_id",
      width: 110,
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <span className={styles.sessionIdChip}>{formatSessionIdShort(val)}</span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: t("engagement_page.colResource"),
      key: "resource",
      width: 130,
      render: (_, record) =>
        record.resource_type && record.resource_id
          ? `${record.resource_type}:${record.resource_id}`
          : record.resource_id || "—",
    },
  ];

  const handleExport = async (format) => {
    if (exporting) return;
    setExporting(true);
    try {
      const { items: exportItems, sessions: exportSessions } = await fetchAllForExport();
      const exportFilters = {
        period_days: PERIOD_DAYS[period] || 30,
        view_mode: viewMode,
        bot_traffic: botTrafficFilter,
        audience: audienceTab,
        filter_user_id: filterUserId || null,
        event_category: categoryFilter || null,
        search: search.trim() || null,
      };

      if (viewMode === "sessions" || viewMode === "visitors") {
        const hasRows = exportSessions.some(
          (s) => filterEngagementTimelineEvents(s.events).length,
        );
        if (!hasRows) {
          notification.warning({ message: t("engagement_page.exportEmpty") });
          return;
        }
        if (format === "json") {
          exportEngagementJson({
            viewMode,
            sessions: exportSessions,
            filters: exportFilters,
          });
        } else if (format === "excel") {
          exportEngagementSessionsExcel(exportSessions, t, locale);
        } else {
          exportEngagementSessionsCsv(exportSessions, t, locale);
        }
      } else {
        const exportEvents = exportItems.filter((row) => row.event_name !== "page_dwell");
        if (!exportEvents.length) {
          notification.warning({ message: t("engagement_page.exportEmpty") });
          return;
        }
        if (format === "json") {
          exportEngagementJson({
            viewMode: "events",
            items: exportEvents,
            filters: exportFilters,
          });
        } else if (format === "excel") {
          exportEngagementExcel(exportEvents, t, locale);
        } else {
          exportEngagementCsv(exportEvents, t, locale);
        }
      }
      notification.success({ message: t("engagement_page.exportSuccess") });
    } catch {
      notification.error({ message: t("engagement_page.exportError") });
    } finally {
      setExporting(false);
    }
  };

  const canExport = (pagination.total || 0) > 0;

  const exportMenuItems = [
    { key: "csv", label: t("engagement_page.exportCsv") },
    { key: "json", label: t("engagement_page.exportJson") },
    { key: "excel", label: t("engagement_page.exportExcel") },
  ];

  const renderBanReason = (reason) => {
    const keyMap = {
      automation_detected: "engagement_page.banReasonAutomation",
      auth_attempt_rate: "engagement_page.banReasonAuthRate",
      ip_banned: "engagement_page.banReasonIpBanned",
      manual: "engagement_page.banReasonManual",
    };
    const key = keyMap[reason];
    return key ? t(key) : reason || "—";
  };

  const blockedSummaryMetrics = [
    { valueKey: "active_bans", labelKey: "statActiveBans", hintKey: "statActiveBansHint" },
    { valueKey: "attempts_in_period", labelKey: "statAuthAttempts", hintKey: "statAuthAttemptsHint" },
    { valueKey: "unique_ips", labelKey: "statUniqueIps", hintKey: "statUniqueIpsHint" },
  ];

  const banColumns = [
    {
      title: t("engagement_page.colIp"),
      dataIndex: "ip",
      key: "ip",
      width: 140,
      render: (val) => <span className={styles.ipCell}>{val || "—"}</span>,
    },
    {
      title: t("engagement_page.colReason"),
      dataIndex: "reason",
      key: "reason",
      width: 160,
      render: (val) => renderBanReason(val),
    },
    {
      title: t("engagement_page.colBannedAt"),
      dataIndex: "banned_at",
      key: "banned_at",
      width: 170,
      render: (val) => formatEngagementDateTime(val, locale),
    },
    {
      title: t("engagement_page.colExpiresAt"),
      dataIndex: "expires_at",
      key: "expires_at",
      width: 170,
      render: (val) => formatEngagementDateTime(val, locale),
    },
    {
      title: t("engagement_page.colAttemptCount"),
      dataIndex: "attempt_count",
      key: "attempt_count",
      width: 90,
      render: (val) => (val != null ? val : "—"),
    },
    {
      title: t("engagement_page.colLastEmail"),
      dataIndex: "last_email",
      key: "last_email",
      ellipsis: true,
      render: (val) => val || "—",
    },
    {
      title: t("engagement_page.colLastEndpoint"),
      dataIndex: "last_endpoint",
      key: "last_endpoint",
      width: 140,
      render: (val) => val || "—",
    },
  ];

  const attemptColumns = [
    {
      title: t("engagement_page.colDate"),
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (val) => formatEngagementDateTime(val, locale),
    },
    {
      title: t("engagement_page.colIp"),
      dataIndex: "ip",
      key: "ip",
      width: 140,
      render: (val) => <span className={styles.ipCell}>{val || "—"}</span>,
    },
    {
      title: t("engagement_page.colUser"),
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      render: (val) => val || "—",
    },
    {
      title: t("engagement_page.colOperation"),
      dataIndex: "operation_type",
      key: "operation_type",
      width: 100,
      render: (val) => val || "—",
    },
    {
      title: t("engagement_page.colLastEndpoint"),
      dataIndex: "endpoint",
      key: "endpoint",
      width: 130,
      render: (val) => val || "—",
    },
    {
      title: t("engagement_page.colReason"),
      dataIndex: "block_reason",
      key: "block_reason",
      width: 150,
      render: (val) => renderBanReason(val),
    },
    {
      title: t("engagement_page.colSuccess"),
      dataIndex: "success",
      key: "success",
      width: 80,
      render: (val) => (val ? "✓" : "—"),
    },
    {
      title: t("engagement_page.colHttpStatus"),
      dataIndex: "http_status",
      key: "http_status",
      width: 70,
      render: (val) => (val != null ? val : "—"),
    },
  ];

  if (forbidden) {
    return (
      <div className={styles.forbidden}>
        <h4 className={styles.title}>{t("engagement_page.title")}</h4>
        <p className={styles.subtitle}>{t("engagement_page.forbidden")}</p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Tabs
        activeKey={audienceTab}
        onChange={setAudienceTab}
        className={styles.audienceTabs}
        items={[
          { key: "known", label: t("engagement_page.tabKnownUsers") },
          { key: "unknown", label: t("engagement_page.tabUnknownUsers") },
          { key: "blocked", label: t("engagement_page.tabBlocked") },
        ]}
      />
      <div className={styles.headerRow}>
        <div>
          <h4 className={styles.title}>{t("engagement_page.title")}</h4>
          <p className={styles.subtitle}>
            {isBlockedView
              ? t("engagement_page.subtitleBlocked")
              : isUnknownAudience
                ? t("engagement_page.subtitleUnknown")
                : t("engagement_page.subtitleKnown")}
          </p>
          {isKnownAudience ? (
            <p className={styles.viewerNote}>{t("engagement_page.knownUsersBotNote")}</p>
          ) : null}
          {!isBlockedView && !isUnknownAudience ? (
            <p className={styles.viewerNote}>{t("engagement_page.viewerExcludedNote")}</p>
          ) : null}
          {!isBlockedView && botTrafficExcluded ? (
            <p className={styles.viewerNote}>{t("engagement_page.botExcludedNote")}</p>
          ) : null}
          {!isBlockedView && botSuspectSessionsInPeriod > 0 ? (
            <p className={styles.viewerNote}>
              {t("engagement_page.botSuspectCountNote", {
                count: botSuspectSessionsInPeriod,
              })}
            </p>
          ) : null}
          {isBlockedView && !mongodbReady ? (
            <p className={styles.viewerNote}>{t("engagement_page.securityMongoUnavailable")}</p>
          ) : null}
        </div>
      </div>

      {isBlockedView ? (
        <>
          <div className={`${styles.summaryGrid} ${styles.summaryGridCompact}`}>
            {blockedSummaryMetrics.map(({ valueKey, labelKey, hintKey }) => (
              <div key={valueKey} className={styles.summaryCard}>
                <span className={styles.summaryLabel}>{t(`engagement_page.${labelKey}`)}</span>
                <span className={styles.summaryHint}>{t(`engagement_page.${hintKey}`)}</span>
                <span className={styles.summaryValue}>
                  {securitySummary ? numberWithCommas(securitySummary[valueKey]) : "—"}
                </span>
              </div>
            ))}
          </div>
          <div className={styles.filters}>
            <Segmented
              value={securityView}
              onChange={setSecurityView}
              options={[
                { value: "bans", label: t("engagement_page.viewBlockedBans") },
                { value: "attempts", label: t("engagement_page.viewBlockedAttempts") },
              ]}
              className={styles.viewToggle}
            />
            <Select
              value={period}
              onChange={setPeriod}
              options={[
                { value: "7", label: t("engagement_page.period7") },
                { value: "30", label: t("engagement_page.period30") },
                { value: "90", label: t("engagement_page.period90") },
              ]}
              className={styles.filterSelect}
            />
            <Button
              type="text"
              icon={<ReloadOutlined />}
              className={styles.refreshBtn}
              onClick={() => fetchSecurityBlocks(pagination.page)}
              aria-label={t("engagement_page.refresh")}
            />
          </div>
          <div className={styles.searchRow}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t("engagement_page.searchPlaceholder")}
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onPressEnter={() => setSearch(searchDraft)}
            />
            <Button type="primary" onClick={() => setSearch(searchDraft)}>
              {t("engagement_page.search")}
            </Button>
          </div>
          <div className={styles.tableCard}>
            <Table
              loading={loading}
              rowKey={(row) =>
                row.ip || row.created_at || `${row.email}-${row.endpoint}`
              }
              columns={securityView === "bans" ? banColumns : attemptColumns}
              dataSource={securityView === "bans" ? ipBans : authAttempts}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    description={
                      securityView === "bans"
                        ? t("engagement_page.emptyBlockedBans")
                        : t("engagement_page.emptyBlockedAttempts")
                    }
                  />
                ),
              }}
              scroll={{ x: 960 }}
            />
            <div className={styles.sessionPagination}>
              <Button
                disabled={pagination.page <= 1 || loading}
                onClick={() => fetchSecurityBlocks(pagination.page - 1)}
              >
                {t("engagement_page.prevPage")}
              </Button>
              <span className={styles.sessionPageLabel}>
                {t("engagement_page.pageLabel", {
                  page: pagination.page,
                  total: Math.max(
                    1,
                    Math.ceil((pagination.total || 0) / (pagination.page_size || 50)),
                  ),
                })}
              </span>
              <Button
                disabled={
                  loading ||
                  pagination.page >=
                    Math.ceil((pagination.total || 0) / (pagination.page_size || 50))
                }
                onClick={() => fetchSecurityBlocks(pagination.page + 1)}
              >
                {t("engagement_page.nextPage")}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
      <div className={styles.summaryGrid}>
        {summaryMetrics.map(({ valueKey, labelKey, hintKey }) => (
          <div key={valueKey} className={styles.summaryCard}>
            <span className={styles.summaryLabel}>{t(`engagement_page.${labelKey}`)}</span>
            <span className={styles.summaryHint}>{t(`engagement_page.${hintKey}`)}</span>
            <span className={styles.summaryValue}>
              {summary ? numberWithCommas(summary[valueKey]) : "—"}
            </span>
          </div>
        ))}
      </div>

      {isUnknownAudience && funnel ? (
        <div className={styles.funnelRow}>
          <div className={styles.funnelCard}>
            <span className={styles.funnelLabel}>{t("engagement_page.funnelLanding")}</span>
            <strong>{numberWithCommas(funnel.landing_sessions || 0)}</strong>
          </div>
          <div className={styles.funnelCard}>
            <span className={styles.funnelLabel}>{t("engagement_page.funnelForm")}</span>
            <strong>{numberWithCommas(funnel.form_interaction_sessions || 0)}</strong>
          </div>
          <div className={styles.funnelCard}>
            <span className={styles.funnelLabel}>{t("engagement_page.funnelAttempt")}</span>
            <strong>{numberWithCommas(funnel.auth_attempt_sessions || 0)}</strong>
          </div>
          <div className={styles.funnelCard}>
            <span className={styles.funnelLabel}>{t("engagement_page.funnelSuccess")}</span>
            <strong>{numberWithCommas(funnel.auth_success_sessions || 0)}</strong>
          </div>
        </div>
      ) : null}

      <div className={`${styles.insightsRow} ${isUnknownAudience ? styles.insightsRowWide : ""}`}>
        <div className={styles.insightCard}>
          <p className={styles.insightTitle}>{t("engagement_page.dailyActiveTitle")}</p>
          <p className={styles.insightHint}>{t("engagement_page.dailyActiveHint")}</p>
          {dailyActive.length ? (
            <div className={styles.barChart}>
              {dailyActive.slice(-14).map((day) => (
                <div key={day.date} className={styles.barCol} title={`${day.date}: ${day.active_users}`}>
                  <div
                    className={styles.barFill}
                    style={{
                      height: `${Math.max(8, (day.active_users / dailyMax) * 100)}%`,
                    }}
                  />
                  <span className={styles.barLabel}>
                    {day.date ? day.date.slice(5) : ""}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("engagement_page.noChartData")} />
          )}
        </div>

        <div className={styles.insightCard}>
          <p className={styles.insightTitle}>{t("engagement_page.topEventsTitle")}</p>
          <ul className={styles.rankList}>
            {topEvents.length ? (
              topEvents.map((row) => (
                <li key={row.event_name} className={styles.rankItem}>
                  <span>{renderEventLabel(row.event_name)}</span>
                  <strong>{numberWithCommas(row.count)}</strong>
                </li>
              ))
            ) : (
              <li className={styles.rankEmpty}>{t("engagement_page.noChartData")}</li>
            )}
          </ul>
        </div>

        <div className={styles.insightCard}>
          <p className={styles.insightTitle}>{t("engagement_page.topPagesTitle")}</p>
          <p className={styles.insightHint}>{t("engagement_page.topPagesHint")}</p>
          <ul className={styles.rankList}>
            {topPages.length ? (
              topPages.map((row) => (
                <li key={row.page_path} className={styles.rankItem}>
                  <span className={styles.pagePathBlock}>
                    <span className={styles.pagePath}>{row.page_path}</span>
                    {row.avg_duration_ms ? (
                      <span className={styles.pagePathMeta}>
                        {t("engagement_page.avgDurationLabel", {
                          duration: formatEngagementDuration(row.avg_duration_ms, t),
                        })}
                        {row.avg_action_count > 0
                          ? ` · ${t("engagement_page.avgActionsLabel", {
                              count: row.avg_action_count,
                            })}`
                          : ""}
                      </span>
                    ) : null}
                  </span>
                  <strong>{numberWithCommas(row.count)}</strong>
                </li>
              ))
            ) : (
              <li className={styles.rankEmpty}>{t("engagement_page.noChartData")}</li>
            )}
          </ul>
        </div>

        {isUnknownAudience ? (
          <div className={styles.insightCard}>
            <p className={styles.insightTitle}>{t("engagement_page.topAuthEventsTitle")}</p>
            <p className={styles.insightHint}>{t("engagement_page.topAuthEventsHint")}</p>
            <ul className={styles.rankList}>
              {topAuthEvents.length ? (
                topAuthEvents.map((row) => (
                  <li key={row.event_name} className={styles.rankItem}>
                    <span>{renderEventLabel(row.event_name)}</span>
                    <strong>{numberWithCommas(row.count)}</strong>
                  </li>
                ))
              ) : (
                <li className={styles.rankEmpty}>{t("engagement_page.noChartData")}</li>
              )}
            </ul>
          </div>
        ) : null}
      </div>

      <div className={styles.filters}>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={
            isUnknownAudience
              ? [
                  { value: "visitors", label: t("engagement_page.viewVisitors") },
                  { value: "sessions", label: t("engagement_page.viewSessions") },
                  { value: "events", label: t("engagement_page.viewEvents") },
                ]
              : [
                  { value: "events", label: t("engagement_page.viewEvents") },
                  { value: "sessions", label: t("engagement_page.viewSessions") },
                ]
          }
          className={styles.viewToggle}
        />
        <Select
          value={period}
          onChange={setPeriod}
          options={[
            { value: "7", label: t("engagement_page.period7") },
            { value: "30", label: t("engagement_page.period30") },
            { value: "90", label: t("engagement_page.period90") },
          ]}
          className={styles.filterSelect}
        />
        {isUnknownAudience ? (
          <Select
            value={botTrafficFilter}
            onChange={setBotTrafficFilter}
            className={styles.filterSelect}
            options={[
              { value: "bot", label: t("engagement_page.botFilterBotOnly") },
              { value: "all", label: t("engagement_page.botFilterAll") },
              { value: "human", label: t("engagement_page.botFilterHuman") },
            ]}
          />
        ) : null}
        {isKnownAudience ? (
          <Select
            allowClear
            placeholder={t("engagement_page.filterUser")}
            className={styles.filterSelect}
            value={filterUserId}
            onChange={setFilterUserId}
            options={filterUsers.map((u) => ({
              value: u.user_id,
              label: u.label || u.email || u.user_id,
            }))}
          />
        ) : null}
        <Select
          allowClear
          placeholder={t("engagement_page.filterCategory")}
          className={styles.filterSelect}
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={Object.entries(ENGAGEMENT_CATEGORY_LABEL_KEYS).map(([value, key]) => ({
            value,
            label: t(key),
          }))}
        />
        <Button
          type="text"
          icon={<ReloadOutlined />}
          className={styles.refreshBtn}
          onClick={() => fetchAnalytics(pagination.page)}
          aria-label={t("engagement_page.refresh")}
        />
        <Dropdown
          menu={{
            items: exportMenuItems,
            onClick: ({ key }) => handleExport(key),
          }}
          disabled={!canExport || exporting}
        >
          <Button icon={<DownloadOutlined />} loading={exporting} disabled={!canExport}>
            {t("engagement_page.export")}
          </Button>
        </Dropdown>
      </div>

      <div className={styles.searchRow}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("engagement_page.searchPlaceholder")}
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onPressEnter={() => setSearch(searchDraft)}
        />
        <Button type="primary" onClick={() => setSearch(searchDraft)}>
          {t("engagement_page.search")}
        </Button>
      </div>

      <div className={styles.tableCard}>
        {viewMode === "visitors" ? (
          <>
            {loading ? (
              <div className={styles.tableLoading}>
                <Spin />
              </div>
            ) : visitors.length ? (
              <div className={styles.sessionList}>
                {visitors.map((visitor) => {
                  const timelineEvents = filterEngagementTimelineEvents(visitor.events);
                  const visitorKey =
                    visitor.visitor_key ||
                    visitor.user_id ||
                    `${visitor.first_seen}-${visitor.last_seen}`;
                  return (
                    <details key={visitorKey} className={styles.sessionCard}>
                      <summary className={styles.sessionSummary}>
                        <div className={styles.sessionSummaryMain}>
                          <span className={styles.sessionSummaryUser}>
                            {isUnknownAudience
                              ? visitor.visitor_label ||
                                (visitor.visitor_key
                                  ? `Visitor ${String(visitor.visitor_key).slice(0, 8)}`
                                  : "—")
                              : visitor.user_display ||
                                visitor.user_email ||
                                visitor.user_id ||
                                "—"}
                          </span>
                          <span className={styles.sessionSummaryMeta}>
                            {formatEngagementDateTime(visitor.first_seen, locale)}
                            {visitor.last_seen && visitor.last_seen !== visitor.first_seen
                              ? ` – ${formatEngagementTime(visitor.last_seen, locale)}`
                              : ""}
                          </span>
                          <span className={styles.visitorAuthSummary}>
                            {summarizeVisitorAuthActivity(visitor, t)}
                          </span>
                        </div>
                        <div className={styles.sessionSummaryAside}>
                          {visitor.bot_suspect ? (
                            <BotSuspectBadge record={visitor} t={t} />
                          ) : null}
                          {visitor.auth_success ? (
                            <span className={styles.visitorSuccessBadge}>
                              {t("engagement_page.authOutcomeSuccess")}
                            </span>
                          ) : null}
                          <span className={styles.sessionEventCount}>
                            {t("engagement_page.visitorMeta", {
                              sessions: visitor.session_count || 0,
                              events: timelineEvents.length,
                            })}
                          </span>
                        </div>
                      </summary>
                      <SessionTimeline
                        session={{ events: visitor.events }}
                        t={t}
                        locale={locale}
                        renderEventLabel={renderEventLabel}
                        renderCategoryLabel={renderCategoryLabel}
                      />
                    </details>
                  );
                })}
              </div>
            ) : (
              <Empty description={t("engagement_page.emptyVisitors")} />
            )}
            <div className={styles.sessionPagination}>
              <Button
                disabled={pagination.page <= 1 || loading}
                onClick={() => fetchAnalytics(pagination.page - 1)}
              >
                {t("engagement_page.prevPage")}
              </Button>
              <span className={styles.sessionPageLabel}>
                {t("engagement_page.pageLabel", {
                  page: pagination.page,
                  total: Math.max(
                    1,
                    Math.ceil((pagination.total || 0) / (pagination.page_size || 20)),
                  ),
                })}
              </span>
              <Button
                disabled={
                  loading ||
                  pagination.page >=
                    Math.ceil((pagination.total || 0) / (pagination.page_size || 20))
                }
                onClick={() => fetchAnalytics(pagination.page + 1)}
              >
                {t("engagement_page.nextPage")}
              </Button>
            </div>
          </>
        ) : viewMode === "sessions" ? (
          <>
            {loading ? (
              <div className={styles.tableLoading}>
                <Spin />
              </div>
            ) : sessions.length ? (
              <div className={styles.sessionList}>
                {sessions.map((session) => (
                  <details key={session.session_id} className={styles.sessionCard}>
                    <summary className={styles.sessionSummary}>
                      <div className={styles.sessionSummaryMain}>
                        <span className={styles.sessionSummaryUser}>
                          {isUnknownAudience
                            ? session.visitor_label ||
                              (session.visitor_key
                                ? `Visitor ${String(session.visitor_key).slice(0, 8)}`
                                : "—")
                            : session.user_display || session.user_email || session.user_id || "—"}
                        </span>
                        <span className={styles.sessionSummaryMeta}>
                          {formatEngagementDateTime(session.started_at, locale)}
                          {session.ended_at && session.ended_at !== session.started_at
                            ? ` – ${formatEngagementTime(session.ended_at, locale)}`
                            : ""}
                        </span>
                      </div>
                      <div className={styles.sessionSummaryAside}>
                        {session.bot_suspect ? (
                          <BotSuspectBadge record={session} t={t} />
                        ) : null}
                        <span className={styles.sessionEventCount}>
                          {t("engagement_page.sessionEventCount", {
                            count: session.event_count || (session.events || []).length,
                          })}
                        </span>
                        <Tooltip title={session.session_id}>
                          <span className={styles.sessionIdChip}>
                            {formatSessionIdShort(session.session_id)}
                          </span>
                        </Tooltip>
                      </div>
                    </summary>
                    <SessionTimeline
                      session={session}
                      t={t}
                      locale={locale}
                      renderEventLabel={renderEventLabel}
                      renderCategoryLabel={renderCategoryLabel}
                    />
                  </details>
                ))}
              </div>
            ) : (
              <Empty description={t("engagement_page.emptySessions")} />
            )}
            <div className={styles.sessionPagination}>
              <Button
                disabled={pagination.page <= 1 || loading}
                onClick={() => fetchAnalytics(pagination.page - 1)}
              >
                {t("engagement_page.prevPage")}
              </Button>
              <span className={styles.sessionPageLabel}>
                {t("engagement_page.pageLabel", {
                  page: pagination.page,
                  total: Math.max(1, Math.ceil((pagination.total || 0) / (pagination.page_size || 20))),
                })}
              </span>
              <Button
                disabled={
                  loading ||
                  pagination.page >=
                    Math.ceil((pagination.total || 0) / (pagination.page_size || 20))
                }
                onClick={() => fetchAnalytics(pagination.page + 1)}
              >
                {t("engagement_page.nextPage")}
              </Button>
            </div>
          </>
        ) : (
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={displayItems}
            locale={{
              emptyText: loading ? (
                <div className={styles.tableLoading}>
                  <Spin size="small" />
                </div>
              ) : (
                <Empty description={t("engagement_page.emptyHistory")} />
              ),
            }}
            expandable={{
              expandedRowRender: renderExpandedRow,
              columnTitle: t("engagement_page.colDetails"),
              columnWidth: 72,
              expandRowByClick: true,
              rowExpandable: (record) =>
                record.properties && Object.keys(record.properties).length > 0,
            }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.page_size,
              total: pagination.total,
              showSizeChanger: false,
              onChange: (page) => fetchAnalytics(page),
            }}
            scroll={{ x: 1480 }}
          />
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default Representation;
